import { DateTime } from 'luxon';

import {
  ExecutionType,
  OriginationType,
  PerformanceClass,
  ServicingPreference,
} from '__generated__/globalTypes';
/**
 * Read-rail vocabulary for offering terms, shared by the deal room's
 * StatusTracker and the listing page's side panels so a buyer sees the same
 * words for the same term on both screens.
 */

export const formatOriginationType = (originationType: OriginationType): string => {
  switch (originationType) {
    case OriginationType.DIRECT:
      return 'Direct';
    case OriginationType.INDIRECT:
      return 'Indirect';
    case OriginationType.INDIRECT_FINTECH:
      return 'Indirect Fintech';
    case OriginationType.MIXED:
      return 'Mixed';
    case OriginationType.N_A:
      return 'N/A';
  }
};

export const formatExecutionType = (executionType: ExecutionType): string => {
  switch (executionType) {
    case ExecutionType.ALL_OR_NOTHING:
      return 'AoN';
    case ExecutionType.BUYER_CHOICE:
      return 'Buyer Choice';
  }
};

export const formatServicingPreference = (servicingPreference: ServicingPreference): string => {
  switch (servicingPreference) {
    case ServicingPreference.NONE:
      return 'No Preference';
    case ServicingPreference.RELEASED:
      return 'Released';
    case ServicingPreference.RETAINED:
      return 'Retained';
  }
};

export const formatPerformanceClass = (performanceClass: PerformanceClass): string => {
  switch (performanceClass) {
    case PerformanceClass.NON_PERFORMING:
      return 'Non-Performing';
    case PerformanceClass.PERFORMING:
      return 'Performing';
    case PerformanceClass.SUB_PERFORMING:
      return 'Sub-Performing';
    case PerformanceClass.RE_PERFORMING:
      return 'Re-Performing';
  }
};

/** Basis points as the rail's fixed two-decimal percent ("25 bps" -> "0.25%"). */
export const formatRateBps = (bps: number): string => `${(bps / 100).toFixed(2)}%`;

/**
 * Whole loan unless the seller set a participation below 100%.
 *
 * Truthy, not nullish: StatusTracker reads a 0 rate as "Whole Loan", and that rail shows the same
 * listing once a deal exists, so a nullish read here would leave the pre-deal panel and the
 * post-deal rail disagreeing about one listing. Call sites that use `!= null` (RequestAccess,
 * bidUtils, BidStipulations) are asking whether the field is set at all, a different question.
 */
export const formatOwnership = (participationRate: number | null | undefined): string =>
  participationRate && participationRate < 100
    ? `Participation (${participationRate}%)`
    : 'Whole Loan';

/**
 * The calendar day the seller picked, e.g. "Sep 12, 2026" — read in UTC, not the viewer's zone.
 *
 * Expiration is stored as an instant at the seller's local midnight, but it means a day, not a
 * moment. Rendering it locally showed a viewer west of the seller the day before: an Eastern
 * seller's Sep 12 is 04:00Z, which a Pacific buyer reads as Sep 11 21:00. UTC returns the seller's
 * day to every viewer, because US local midnight lands at 04:00-10:00Z on that same date. A seller
 * east of UTC would shift the other way; there are none today, and fixing that properly means
 * storing a date rather than an instant.
 */
export const formatExpirationDate = (iso: string): string =>
  DateTime.fromISO(iso, { zone: 'utc' }).toLocaleString(DateTime.DATE_MED);
